if (!customElements.get('product-form')) {
  // Comforts Kids: declared inside Dawn's load-once guard. A top-level const would throw "already declared"
  // when a second copy of this file loads (featured product, collections, quick add).
  const CK_ORDER_ERROR = 'Something went wrong. Please tap Order now again, or order on WhatsApp.';
  const CK_ADD_ERROR = 'Something went wrong. Please tap Add to cart again, or order on WhatsApp.';

  customElements.define(
    'product-form',
    class ProductForm extends HTMLElement {
      constructor() {
        super();

        this.form = this.querySelector('form');
        this.variantIdInput.disabled = false;
        this.form.addEventListener('submit', this.onSubmitHandler.bind(this));
        this.cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        this.submitButton = this.querySelector('[name="add"]') || this.querySelector('[type="submit"]');
        this.orderButton = this.querySelector('[data-ck-order]');
        this.submitButtonText = this.submitButton.querySelector('span');

        if (document.querySelector('cart-drawer')) this.submitButton.setAttribute('aria-haspopup', 'dialog');

        this.hideErrors = this.dataset.hideErrors === 'true';

        // Comforts Kids: remember the submit button that was clicked. Browsers without SubmitEvent.submitter
        // (iOS Safari before 15.4) would otherwise treat Order now as Add to cart. The submit event fires
        // inside the click, so the note is cleared right after it.
        this.form.addEventListener(
          'click',
          (event) => {
            const button = event.target.closest ? event.target.closest('button, input[type="submit"]') : null;
            if (!button || button.type !== 'submit' || !this.form.contains(button)) return;
            this.clickedSubmit = button;
            setTimeout(() => {
              if (this.clickedSubmit === button) this.clickedSubmit = null;
            });
          },
          true
        );

        // Comforts Kids: Back from checkout restores this page from the back-forward cache with the
        // pressed button still busy. Reset it so Order now (buy box, hero, sticky bar) works again.
        window.addEventListener('pageshow', (event) => {
          if (event.persisted && this.isConnected) this.resetBusyState();
        });
      }

      resetBusyState() {
        [this.orderButton, this.submitButton].forEach((btn) => {
          if (!btn) return;
          btn.classList.remove('loading');
          btn.removeAttribute('aria-busy');
          if (btn !== this.submitButton || !this.error) btn.removeAttribute('aria-disabled');
          const spinner = btn.querySelector('.loading__spinner');
          if (spinner) spinner.classList.add('hidden');
        });
      }

      onSubmitHandler(evt) {
        evt.preventDefault();
        // Comforts Kids: two submit buttons. The pressed one (Order now or Add to cart) drives the flow.
        const clicked = this.clickedSubmit && this.form.contains(this.clickedSubmit) ? this.clickedSubmit : null;
        this.clickedSubmit = null;
        const pressed =
          evt.submitter && this.form.contains(evt.submitter) ? evt.submitter : clicked || this.submitButton;
        const goCheckout = pressed.hasAttribute('data-ck-order');
        if (pressed.getAttribute('aria-disabled') === 'true') return;
        const pressedSpinner = pressed.querySelector('.loading__spinner') || this.querySelector('.loading__spinner');

        this.handleErrorMessage();

        pressed.setAttribute('aria-disabled', true);
        pressed.setAttribute('aria-busy', 'true');
        pressed.classList.add('loading');
        if (pressedSpinner) pressedSpinner.classList.remove('hidden');

        if (goCheckout) {
          this.orderNow(pressed, pressedSpinner);
          return;
        }

        const config = fetchConfig('javascript');
        config.headers['X-Requested-With'] = 'XMLHttpRequest';
        delete config.headers['Content-Type'];

        const formData = new FormData(this.form);
        if (this.cart) {
          formData.append(
            'sections',
            this.cart.getSectionsToRender().map((section) => section.id)
          );
          formData.append('sections_url', window.location.pathname);
          this.cart.setActiveElement(document.activeElement);
        }
        config.body = formData;

        const variantId = formData.get('id');
        const quantity = parseInt(formData.get('quantity')) || 1;
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);

        fetch(`${routes.cart_add_url}`, config)
          .then((response) => response.json())
          .then((response) => {
            if (response.status) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: response.errors || response.description,
                message: response.message,
              });
              this.handleErrorMessage(response.description || CK_ADD_ERROR);
              this.dispatchCartErrorEvent(response.description || response.message, 'INVALID');
              linesUpdateDeferred?.reject(new Error(response.description || response.message));

              const soldOutMessage = pressed === this.submitButton && this.submitButton.querySelector('.sold-out-message');
              if (!soldOutMessage) return;
              this.submitButton.setAttribute('aria-disabled', true);
              this.submitButtonText.classList.add('hidden');
              soldOutMessage.classList.remove('hidden');
              this.error = true;
              return;
            } else if (!this.cart) {
              this.resolveCartLinesUpdate(linesUpdateDeferred);
              window.location = window.routes.cart_url;
              return;
            }

            this.resolveCartLinesUpdate(linesUpdateDeferred);

            const startMarker = CartPerformance.createStartingMarker('add:wait-for-subscribers');
            if (!this.error)
              publish(PUB_SUB_EVENTS.cartUpdate, {
                source: 'product-form',
                productVariantId: variantId,
                cartData: response,
              }).then(() => {
                CartPerformance.measureFromMarker('add:wait-for-subscribers', startMarker);
              });
            this.error = false;
            const quickAddModal = this.closest('quick-add-modal');
            if (quickAddModal) {
              document.body.addEventListener(
                'modalClosed',
                () => {
                  setTimeout(() => {
                    CartPerformance.measure("add:paint-updated-sections", () => {
                      this.cart.renderContents(response);
                    });
                  });
                },
                { once: true }
              );
              quickAddModal.hide(true);
            } else {
              CartPerformance.measure("add:paint-updated-sections", () => {
                this.cart.renderContents(response);
              });
            }
          })
          .catch((e) => {
            console.error(e);
            this.handleErrorMessage(CK_ADD_ERROR);
            this.dispatchCartErrorEvent(e.message || 'Network error', 'SERVICE_UNAVAILABLE');
            linesUpdateDeferred?.reject(e);
          })
          .finally(() => {
            pressed.classList.remove('loading');
            pressed.removeAttribute('aria-busy');
            if (this.cart && this.cart.classList.contains('is-empty')) this.cart.classList.remove('is-empty');
            if (!this.error || pressed !== this.submitButton) pressed.removeAttribute('aria-disabled');
            if (pressedSpinner) pressedSpinner.classList.add('hidden');

            CartPerformance.measureFromEvent("add:user-action", evt);
          });
      }

      // Comforts Kids (D-13): Order now SETS the chosen design's quantity (/cart/update.js) instead of adding
      // on top, so a second tap after Back, or an old cart, cannot stack extra blankets into the Cash on
      // Delivery checkout. With only this design in the cart it goes straight to /checkout; with other
      // designs too (D-17 mixed carts) it opens the cart drawer so the parent can check the whole order.
      orderNow(pressed, pressedSpinner) {
        const formData = new FormData(this.form);
        const variantId = formData.get('id');
        const quantity = Math.max(1, parseInt(formData.get('quantity')) || 1);
        const linesUpdateDeferred = this.createCartLinesUpdateEvent(variantId, quantity);
        let navigating = false;
        let errorMessage = null;

        const fail = (message, code) => {
          errorMessage = message;
          this.handleErrorMessage(message);
          this.dispatchCartErrorEvent(message, code);
          linesUpdateDeferred?.reject(new Error(message));
        };

        fetch(`${routes.cart_update_url}.js`, {
          ...fetchConfig(),
          body: JSON.stringify({ updates: { [variantId]: quantity } }),
        })
          .then((response) => response.json())
          .then((cart) => {
            if (!cart || cart.status || !Array.isArray(cart.items)) {
              publish(PUB_SUB_EVENTS.cartError, {
                source: 'product-form',
                productVariantId: variantId,
                errors: cart && (cart.errors || cart.description),
                message: cart && cart.message,
              });
              return fail((cart && cart.description) || CK_ORDER_ERROR, 'INVALID');
            }
            const line = cart.items.find((item) => String(item.variant_id) === String(variantId));
            if (!line) return fail(CK_ORDER_ERROR, 'INVALID');
            if (line.quantity < quantity) {
              // Shopify capped the line at the stock left: say so instead of checking out a different order.
              return fail(`Only ${line.quantity} left in this design.`, 'INVALID');
            }

            this.resolveCartLinesUpdateWith(linesUpdateDeferred, cart);
            const others = cart.items.some((item) => String(item.variant_id) !== String(variantId));
            if (others && this.cart && this.cart.tagName === 'CART-DRAWER') return this.openCartDrawer(cart, variantId);

            navigating = true;
            const target = others ? window.routes.cart_url : '/checkout';
            const settled = linesUpdateDeferred
              ? Promise.race([
                  linesUpdateDeferred.promise.catch(() => {}),
                  new Promise((resolve) => setTimeout(resolve, 600)),
                ])
              : Promise.resolve();
            return settled.then(() => {
              window.location.assign(target);
            });
          })
          .catch((e) => {
            console.error(e);
            fail(CK_ORDER_ERROR, 'SERVICE_UNAVAILABLE');
          })
          .finally(() => {
            if (navigating) return; // page is leaving: stay busy, block double taps
            pressed.classList.remove('loading');
            pressed.removeAttribute('aria-busy');
            pressed.removeAttribute('aria-disabled');
            if (pressedSpinner) pressedSpinner.classList.add('hidden');
            // The hero and sticky Order now forward here: let them clear their busy state and show errors.
            this.dispatchEvent(new CustomEvent('ck:order-done', { bubbles: true, detail: { error: errorMessage } }));
          });
      }

      openCartDrawer(cart, variantId) {
        this.cart.setActiveElement(document.activeElement);
        const ids = this.cart.getSectionsToRender().map((section) => section.id);
        return fetch(`${routes.cart_url}?sections=${ids.join(',')}`)
          .then((response) => response.json())
          .then((sections) => {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'product-form', productVariantId: variantId, cartData: cart });
            this.cart.classList.remove('is-empty');
            this.cart.renderContents({ ...cart, sections });
          });
      }

      handleErrorMessage(errorMessage = false) {
        if (this.hideErrors) return;

        this.errorMessageWrapper =
          this.errorMessageWrapper || this.querySelector('.product-form__error-message-wrapper');
        if (!this.errorMessageWrapper) return;
        this.errorMessage = this.errorMessage || this.errorMessageWrapper.querySelector('.product-form__error-message');

        this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage);

        if (errorMessage) {
          this.errorMessage.textContent = errorMessage;
        }
      }

      toggleSubmitButton(disable = true, text) {
        if (disable) {
          this.submitButton.setAttribute('disabled', 'disabled');
          if (this.orderButton) this.orderButton.setAttribute('disabled', 'disabled');
          if (text) this.submitButtonText.textContent = text;
        } else {
          this.submitButton.removeAttribute('disabled');
          if (this.orderButton) this.orderButton.removeAttribute('disabled');
          this.submitButtonText.textContent = window.variantStrings.addToCart;
        }
      }

      createCartLinesUpdateEvent(variantId, quantity) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return null;

        const deferred = CartLinesUpdateEvent.createPromise();
        this.dispatchEvent(
          new CartLinesUpdateEvent({
            action: 'add',
            context: 'product',
            lines: [{ merchandiseId: variantId, quantity }],
            promise: deferred.promise,
          })
        );
        return deferred;
      }

      resolveCartLinesUpdate(deferred) {
        if (!deferred) return;
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!CartLinesUpdateEvent) return;

        const pendingCartDataPromise = typeof CartItems !== 'undefined'
          ? CartItems.fetchCartData()
          : fetch(`${routes.cart_url}.json`).then((response) => response.json());

        pendingCartDataPromise
          .then((cart) => {
            if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
            deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
          })
          .catch((e) => deferred.reject(e));
      }

      resolveCartLinesUpdateWith(deferred, cart) {
        const { CartLinesUpdateEvent } = window.StandardEvents || {};
        if (!deferred || !CartLinesUpdateEvent) return;
        if (!cart?.currency) return deferred.reject(new Error('Missing currency in cart response'));
        deferred.resolve({ cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart) });
      }

      dispatchCartErrorEvent(message, code) {
        const { CartErrorEvent } = window.StandardEvents || {};
        if (!CartErrorEvent) return;
        this.dispatchEvent(new CartErrorEvent({ error: message, code }));
      }

      get variantIdInput() {
        return this.form.querySelector('[name=id]');
      }
    }
  );
}
